import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function EdinburghPage() {
  return (
    <>
      <LocalBusinessSchema city="Edinburgh" latitude={55.9533} longitude={-3.1883} />
      <PageHero
        title="Fractional CISO Services in Edinburgh"
        subtitle="Cybersecurity leadership for Scotland's financial and data capital"
        eyebrow="Edinburgh"
        seoTitle="Fractional CISO Edinburgh | The CISO Network"
        seoDescription="Expert fractional CISO services for Edinburgh businesses. Access senior cybersecurity leadership for Scotland's fintech and data sectors."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Scotland's Financial Powerhouse
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Edinburgh stands as Europe's second-largest financial centre and Scotland's
                  undisputed data capital. The city's historic financial district, combined with
                  thriving tech clusters around the Bay Area and city centre, creates a unique
                  business ecosystem where tradition meets innovation. From established banks to
                  ambitious fintech startups, Edinburgh businesses handle vast amounts of sensitive
                  financial data daily.
                </p>
                <p>
                  The regulatory demands on Edinburgh businesses are intense. Financial services
                  firms must navigate FCA, PRA, and Scottish regulatory requirements. The city's
                  booming data science and AI sector handles sensitive datasets requiring robust
                  protection. Meanwhile, the growing number of scale-ups emerging from Edinburgh's
                  universities need security programmes that satisfy enterprise clients and
                  investors.
                </p>
                <p>
                  Despite this sophisticated business environment, many Edinburgh companies struggle
                  to access the C-level security expertise they need. Full-time CISOs command
                  premium salaries in this competitive market, and the talent pool is limited. Our
                  fractional CISO service brings executive security leadership to Edinburgh
                  businesses on flexible terms that match their growth stage.
                </p>
                <p>
                  Edinburgh hosts Scotland's financial services district, major government bodies,
                  and a rapidly growing technology sector. The city's businesses face some of the
                  most complex regulatory requirements in the UK, from FCA oversight to Scottish
                  public sector standards. A fractional CISO with Edinburgh market experience
                  understands these overlapping demands.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands Scotland's unique financial services
                      landscape and regulatory environment.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Central Scotland Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Edinburgh, Glasgow, and the Central Belt with
                      local CISO expertise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Edinburgh Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Financial services-grade security leadership for Scotland's data capital
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'FCA Expertise',
                description:
                  'Navigate complex financial services regulations with experienced CISO guidance.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  "Access senior expertise without Edinburgh's premium CISO salary expectations.",
              },
              {
                icon: Users,
                title: 'Financial Sector Knowledge',
                description:
                  'CISOs with deep experience in banking, fintech, insurance, and asset management.',
              },
              {
                icon: Clock,
                title: 'Scale-Up Ready',
                description:
                  'Security programmes that grow with your Edinburgh business and satisfy investors.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Edinburgh - FAQs"
        subtitle="Common questions from Edinburgh and Scottish businesses"
        items={[
          {
            id: 'edinburgh-1',
            question: 'How does a fractional CISO work with businesses in Edinburgh?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Edinburgh. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing finance, public sector, and technology companies.',
          },
          {
            id: 'edinburgh-2',
            question: 'What size of business benefits from a fractional CISO in Edinburgh?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Edinburgh startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'edinburgh-3',
            question: 'What industries do your CISOs cover in Edinburgh?',
            answer:
              "Our network includes CISOs with experience in finance, public sector, and technology. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'edinburgh-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'edinburgh-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Edinburgh Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your financial
            services or data business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
